import React, { FunctionComponent, useCallback } from 'react';
import { Table, Typography, Space, Button, Tooltip, Popconfirm } from 'antd';
import { useStore } from 'effector-react';
import routes from '@config/routes';
import users, { blockUser, unblockUser, deleteUser, getList } from '@stores/users';
import auth from '@stores/auth';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import IUser from '@interfaces/IUser';
import useTranslate from '@hooks/useTranslate';
import componentContent from './UsersTable.content.json';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import css from './UsersTable.module.less';
import {
  LockOutlined,
  UnlockOutlined,
  UserDeleteOutlined,
  UserAddOutlined,
  EditOutlined,
} from '@ant-design/icons';
import Avatar from '@components/Avatar';
import UserBlockMenu from '@components/UserBlockMenu';
import { Link } from 'react-router-dom';
import { getPath } from '@helpers/navigation';
import { pageSizeOptions } from '@constants/pagination';
import { SortOrder } from 'antd/lib/table/interface';
import useScrollTopPage from '@hooks/useScrollTopPage';
import { openModal } from '@stores/modals';
import useGuards from '@hooks/useGuards';
import ROLES from '@constants/roles';
import moment from 'moment';
import { DATE_FORMAT } from '@config/format';
import type { UserBlockReason } from '@constants/userBlockOptions';

interface UsersTableProps {}

const UsersTable: FunctionComponent<UsersTableProps> = () => {
  const { list, preloaders, pagination, filters: currentFilterParams } = useStore(users);
  const { user: authUser } = useStore(auth);
  const content = useTranslate(componentContent);

  const { allowCRUD, isDeveloper, isModerator } = useGuards();

  const onBlock = useCallback(
    (user: IUser, daysToUnblock?: number, daysToClean?: number, reason?: UserBlockReason) => {
      blockUser({
        userId: user.id,
        preloaderId: user.id,
        user,
        daysToUnblock,
        daysToClean,
        reason,
      });
    },
    [],
  );

  const onChange = useCallback((pagination: TablePaginationConfig, filters: any, sorter: any) => {
    localStorage.setItem('usersPageSize', `${pagination.pageSize}`);
    getList({
      pagination,
      sorter,
    });
  }, []);

  useScrollTopPage(pagination);

  const [currentSortedColumnKey, sortDirection] = (currentFilterParams.sort || '').split(',');

  const deleteUserHandler = useCallback((user: IUser) => {
    deleteUser(user.id);
  }, []);

  const createEditUserHandler = useCallback((user?: IUser) => {
    openModal({ name: 'user', data: user });
  }, []);

  const createUserButton = useCallback(() => {
    return (
      <Button
        onClick={() => createEditUserHandler()}
        icon={<UserAddOutlined />}
      >
        {content.addUser}
      </Button>
    );
  }, [content.addUser, createEditUserHandler]);

  const openAccountsByRegistrationIp = useCallback((registrationIp?: string) => {
    if (!registrationIp) return;

    window.open(
      getPath(routes.userList.name, undefined, { registrationIp }),
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  const columns: ColumnsType<IUser> = [
    {
      dataIndex: 'photo',
      width: 48,
      title: '',
      className: css.avatarCell,
      render: (key: string, user: IUser) => <Avatar user={user} />,
    },
    {
      dataIndex: 'nickname',
      key: 'nickname',
      title: content.nickname,
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'nickname' ? (`${sortDirection}end` as SortOrder) : undefined,
      render: (nickname: string, user: IUser) => (
        <Link
          to={getPath('userDetail', { userId: user.id })}
          className='clickable'
        >
          {nickname}
        </Link>
      ),
    },
    {
      key: 'discord',
      dataIndex: 'linkedServices',
      title: 'Discord',
      render: (linkedServices: IUser['linkedServices']) =>
        linkedServices?.find((service) => service.provider === 'DISCORD')?.username || '—',
    },
    {
      dataIndex: 'email',
      title: 'Email',
      render: (email: string) => email && <Typography.Text copyable>{email}</Typography.Text>,
    },
    {
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      title: content.registrationDate,
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'registrationDate'
          ? (`${sortDirection}end` as SortOrder)
          : undefined,
      render: (registrationDate: string) => moment(registrationDate).format(DATE_FORMAT),
    },
    {
      dataIndex: 'registrationIp',
      key: 'registrationIp',
      title: content.registrationIp,
      render: (registrationIp?: string) =>
        registrationIp ? (
          <Typography.Link onClick={() => openAccountsByRegistrationIp(registrationIp)}>
            {registrationIp}
          </Typography.Link>
        ) : (
          '—'
        ),
    },
    {
      dataIndex: 'phone',
      title: content.phone,
      render: (phoneNumber: string) =>
        phoneNumber && parsePhoneNumberFromString(phoneNumber)?.formatInternational(),
    },
    {
      key: 'locales',
      dataIndex: 'locales',
      title: content.locales,
      render: (locales: string[]) => locales?.join(' '),
    },
    {
      key: 'gamesCount',
      dataIndex: 'gamesCount',
      title: content.games,
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'gamesCount' ? (`${sortDirection}end` as SortOrder) : undefined,
      showSorterTooltip: false,
    },
    {
      key: 'role',
      dataIndex: 'role',
      title: content.role,
      render: (role: string) => content[role],
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'role' ? (`${sortDirection}end` as SortOrder) : undefined,
      showSorterTooltip: false,
    },
    {
      render: (_, user: IUser) => {
        const isBlocked = !user.enable;
        const isBlocking = preloaders.blockUser === user.id;
        const isUnblocking = preloaders.unblockUser === user.id;
        const isBlockProcessing = isBlocking || isUnblocking;

        const isUser = user.role === ROLES.USER;
        const isChangingStatus =
          preloaders.makeUser === user.id || preloaders.makeAdmin === user.id;

        const isMe = authUser?.id === user.id;

        return (
          <Space>
            {isUser && !isMe ? (
              <Tooltip title={isBlocked ? content.unblock : content.block}>
                {isBlocked ? (
                  <Popconfirm
                    placement='left'
                    title={content.unblockConfirmText}
                    onConfirm={() =>
                      unblockUser({
                        userId: user.id,
                        preloaderId: user.id,
                        user,
                      })
                    }
                  >
                    <Button
                      disabled={isBlockProcessing}
                      loading={isBlockProcessing}
                      icon={<UnlockOutlined />}
                      shape='circle'
                    />
                  </Popconfirm>
                ) : (
                  <UserBlockMenu
                    user={user}
                    onBlock={onBlock}
                  >
                    <Button
                      disabled={isBlockProcessing}
                      loading={isBlockProcessing}
                      icon={<LockOutlined />}
                      shape='circle'
                    />
                  </UserBlockMenu>
                )}
              </Tooltip>
            ) : null}
            {!isMe && allowCRUD ? (
              <Tooltip title={content.deleteUser}>
                <Popconfirm
                  placement='left'
                  title={content.deleteUserConfirmText}
                  onConfirm={() => deleteUserHandler(user)}
                >
                  <Button
                    disabled={isChangingStatus}
                    loading={isChangingStatus}
                    icon={<UserDeleteOutlined />}
                    shape='circle'
                  />
                </Popconfirm>
              </Tooltip>
            ) : null}
            {!isMe && (allowCRUD || isModerator) ? (
              <Tooltip title={content.editUser}>
                <Button
                  disabled={isChangingStatus}
                  loading={isChangingStatus}
                  onClick={() => createEditUserHandler(user)}
                  icon={<EditOutlined />}
                  shape='circle'
                />
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
  ];

  const filteredColumns = columns.filter((column: any) => {
    if (isDeveloper && (column.dataIndex === 'phone' || column.dataIndex === 'email')) {
      return false;
    }
    return true;
  });

  return (
    <div>
      <Table
        title={allowCRUD ? createUserButton : undefined}
        onChange={onChange}
        dataSource={list}
        columns={filteredColumns}
        loading={!!preloaders.getList}
        rowKey='id'
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions,
          position: ['topRight', 'bottomRight'],
        }}
      />
    </div>
  );
};

export default UsersTable;
